import React, { FC, useContext, useMemo, useState } from "react";
import { Font, Page, Text, View, Document, StyleSheet, Image, pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import zioncom from 'images/system/zioncom.png'
import { App, Button, Dropdown, notification, Tooltip } from "antd";
import { getFileUrl } from "@/apis/file";
import { DictContext } from "@/layout/context/DictContext";
import { SaleItemState, SaleOrderItemState } from "@/types/sales";
import { numFormmater } from "@/pages/Requirement/util";
import { divided, minus, noRateAmount } from "@/utils/calc";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import { dateFormat, priceFormat } from "@/utils/format";
import { computedPrint, initPrintSetting } from "@/layout/HomeLayout/utils";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // ==================== 页面 ====================
  // 页面基础样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
  },
  // 页面容器 - 横向 A4 内边距
  pageWraperLandscape: {
    padding: 28,
  },
  // 页面容器 - 竖向纸张内边距
  pageWraperPortrait: {
    paddingHorizontal: 4,
  },

  // ==================== 标题区 ====================
  // 页面主标题（公司名）
  headerTitle: {
    fontSize: 14,
    textAlign: 'center',
    paddingTop: 8,
  },
  // 副标题（单据类型/单号等）
  subTitle: {
    fontSize: 10,
    textAlign: 'center',
  },
  // 标题区域容器
  titleSection: {
    position: 'relative' as any,
    marginBottom: 6
  },

  // ==================== 弹性布局 ====================
  // 横向弹性容器
  flexRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  // 弹性子项（均分）
  flexRowItem: {
    flex: 1,
  },
  // flexItem字体包
  flexInfoText: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    fontSize: 8,
  },

  bottomFlexItem: {
    width: "20%",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },
  // 签核项内的签名线（撑满剩余空间 + 下边框；react-pdf 中空 View 高度为 0，需显式指定高度边框才可见）
  signText: {
    fontSize: 6
  },
  signLine: {
    flex: 1,
    height: 6,
    borderBottomWidth: 1,
    borderStyle: "solid",
    borderColor: "black",
    marginLeft: 2,
    marginRight: 4
  },

  // ==================== 表格 ====================
  table: {
    display: "table" as any,
    width: "100%",
    flexShrink: 0,
    borderStyle: "solid",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderColor: "black"
  },
  // 表格行
  tableRow: {
    flexDirection: "row",
  },
  // 单元格基础样式
  tableCol: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 2,
    fontSize: 6,
    color: 'black',
    borderLeftWidth: 1,
  },

  // ==================== 边框修饰（配合样式数组使用，覆盖 tableCol 的单边边框） ====================
  // 无边框
  noBoarder: {
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  // 左边框
  leftBoarder: {
    borderLeftWidth: 1,
  },
  // 上边框
  topBoarder: {
    borderTopWidth: 1,
  },
  // 右边框
  rightBoarder: {
    borderRightWidth: 1,
  },
  // 下边框
  bottomBoarder: {
    borderBottomWidth: 1,
  },
  // 去掉左边框
  noLeftBoarder: {
    borderLeftWidth: 0,
  },
  // 去掉上边框
  noTopBoarder: {
    borderTopWidth: 0,
  },
  // 去掉右边框
  noRightBoarder: {
    borderRightWidth: 0,
  },
  // 去掉下边框
  noBottomBoarder: {
    borderBottomWidth: 0,
  },

  // ==================== 单元格文本 ====================
  // 表头单元格文本
  tableHeadertext: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    fontSize: 6,
    textAlign: 'center',
  },
  // 数据单元格文本
  tableCelltext: {
    width: "100%",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    fontSize: 6,
    textAlign: 'left',
  },

  // ==================== 底部 ====================
  // 底部信息容器
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24
  },
  // 底部文本
  bottomText: {
    fontSize: 8
  },
});

interface PrintSaleOrderProps {
  selectedRows: Array<SaleOrderItemState>;
  disabled: boolean;
  onStart: () => void;
  onEnd: () => void;
}


// 生成 PDF 打印链接
const PrintSaleOrder: FC<PrintSaleOrderProps> = ({
  selectedRows, disabled, onStart, onEnd
}) => {
  const { printSetting } = useContext(DictContext);

  const { t } = useTranslation();
  const curUser = useSelector((state: RootState) => state.userReducer.curUser)

  const { width: printWitdh, height: printHeight } = computedPrint();
  const { width: settingWitdh, height: settingHeight } = initPrintSetting();

  const { orderNumber, customerName, licenseId, orderDate, sendAddress, phone, invoiceNumber, invoiceAddress, contactor, remark, totalQuantity, totalAmount, taxAmount, totalRealAmount, fax } = useMemo(() => {
    const order = selectedRows[0];
    return {
      orderNumber: order?.number ?? '',
      customerName: `${order?.customer?.name1 ?? ''}${order?.customer?.name2 ?? ''}`,
      licenseId: order?.customer?.licenseId ?? '',
      orderDate: order?.createdAt ? dayjs(order.createdAt).format('YYYY/MM/DD') : '',
      sendAddress: order?.receiverAddress ?? '',
      phone: order?.customer?.phone || '',
      invoiceNumber: order?.invoiceNumber ?? '',
      invoiceAddress: order?.customer?.invoiceAddress ?? '',
      contactor: order?.customer?.contact ?? '',
      remark: order?.remark ?? '',
      totalQuantity: (order?.items ?? []).reduce((sum, item) => sum + (Number(item.quantity) || 0) + (Number(item.bquantity) || 0), 0),
      totalAmount: order?.totalAmount ?? '',
      taxAmount: minus(order?.totalRealAmount ?? '0', order?.totalAmount ?? '0'),
      totalRealAmount: order?.totalRealAmount ?? '',
      fax: order?.customer?.fax ?? order?.fax ?? '',
    };
  }, [selectedRows]);

  const [loading, setLoading] = useState<boolean>(false)

  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting])

  // 横向打印文档
  const MyDocument = (selRows: SaleItemState[]) => {
    if (selRows && selRows.length > 0) {
      return <Document>
        <Page size="A4" orientation="landscape" style={[styles.page, styles.pageWraperLandscape]} wrap>
          {/* 标题部分 */}
          <View style={styles.titleSection}>
            <Text style={styles.headerTitle}>{name ?? '吉昂科技（深圳）有限公司'}</Text>
            <Text style={styles.subTitle}>{t('sale.domesticSaleOrder')}</Text>
          </View>

          {/* 制表日期 / 页次 */}
          <View style={[styles.flexRow, { marginBottom: 4 }]}>
            <View style={styles.flexRowItem}>
              <Text style={styles.flexInfoText}>{`${t('sale.printDate')}: ${dayjs().format('YYYY/MM/DD')}`}</Text>
            </View>
            <View style={styles.flexRowItem}>
              <Text style={[styles.flexInfoText, { textAlign: 'right' }]} render={({ pageNumber, totalPages }) => `${t('sale.printPageNo')}: ${pageNumber}/${totalPages}`}>{''}</Text>
            </View>
          </View>

          {/* 表格 */}
          <View style={styles.table}>
            {/* 第一行：销货单号 / 客户全名 / 统一称号 */}
            <View style={[styles.tableRow, styles.topBoarder]} wrap={false}>
              <View style={[styles.tableCol, styles.noBottomBoarder, { width: '20%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printSaleNo')}：${orderNumber}`}</Text>
              </View>
              <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, { width: '50%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printCustomerName')}：${customerName}`}</Text>
              </View>
              <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, styles.rightBoarder, { width: '30%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printUnifiedTitle')}：${licenseId}`}</Text>
              </View>
            </View>
            {/* 第二行：单据日期 / 送货地址 / 联系电话 */}
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCol, styles.noBottomBoarder, { width: '20%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printOrderDate')}：${orderDate}`}</Text>
              </View>
              <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, { width: '50%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printSendAddress')}：${sendAddress}`}</Text>
              </View>
              <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, styles.rightBoarder, { width: '30%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printPhone')}：${phone}`}</Text>
              </View>
            </View>
            {/* 第三行：发票号码 / 发票地址 / 联系人 */}
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCol, styles.noBottomBoarder, { width: '20%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printInvoiceNumber')}：${invoiceNumber}`}</Text>
              </View>
              <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, { width: '50%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printInvoiceAddress')}：${invoiceAddress}`}</Text>
              </View>
              <View style={[styles.tableCol, styles.noLeftBoarder, styles.rightBoarder, { width: '30%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printContact')}：${contactor}`}</Text>
              </View>
            </View>
            {/* 第四行：备注 */}
            <View style={[styles.tableRow, styles.bottomBoarder]} wrap={false}>
              <View style={[styles.tableCol, styles.rightBoarder, { width: '100%' }]}>
                <Text style={styles.tableCelltext}>{`${t('sale.printRemark')}：${remark}`}</Text>
              </View>
            </View>
            {/* 第五行：明细表头 */}
            <View style={[styles.tableRow, styles.bottomBoarder]} wrap={false}>
              <View style={[styles.tableCol, { width: '6%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printSeq')}</Text>
              </View>
              <View style={[styles.tableCol, { width: '30%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printProductName')}</Text>
              </View>
              <View style={[styles.tableCol, { width: '10%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printQuantity')}</Text>
              </View>
              <View style={[styles.tableCol, { width: '10%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printBQuantity')}</Text>
              </View>
              <View style={[styles.tableCol, { width: '8%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printUom')}</Text>
              </View>
              <View style={[styles.tableCol, { width: '10%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printPrice')}</Text>
              </View>
              <View style={[styles.tableCol, { width: '12%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printAmount')}</Text>
              </View>
              <View style={[styles.tableCol, styles.rightBoarder, { width: '14%' }]}>
                <Text style={styles.tableHeadertext}>{t('sale.printRemark')}</Text>
              </View>
            </View>
            {/* 明细数据行 */}
            {selRows.map((item, rowIndex) => {
              let taxRate = Number(item?.order?.taxRate) ?? 0;
              let noRateSInfo = '';
              if (taxRate !== 0) {
                noRateSInfo = priceFormat(noRateAmount(item?.price ?? "", divided(taxRate + '', "100")))
              } else noRateSInfo = item?.price ?? "";
              let noRateInfo = '';
              if (taxRate !== 0) {
                noRateInfo = priceFormat(noRateAmount(item?.totalRealAmount ?? "", divided(taxRate + '', "100")))
              } else noRateInfo = item?.totalRealAmount ?? "";
              return (
                <View style={styles.tableRow} key={item.id ?? rowIndex} wrap={false}>
                  <View style={[styles.tableCol, { width: '6%' }]}>
                    <Text style={styles.tableCelltext}>{String(rowIndex + 1).padStart(4, '0')}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '30%' }]}>
                    <Text style={styles.tableCelltext}>{item?.product?.name ?? item.productName ?? ''}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={styles.tableCelltext}>{numFormmater(Number(item.quantity) || 0)}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={styles.tableCelltext}>{numFormmater(Number(item.bquantity) || 0)}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '8%' }]}>
                    <Text style={styles.tableCelltext}>{item.product?.uom ?? ''}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={styles.tableCelltext}>{noRateSInfo}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: '12%' }]}>
                    <Text style={styles.tableCelltext}>{noRateInfo}</Text>
                  </View>
                  <View style={[styles.tableCol, styles.rightBoarder, { width: '14%' }]}>
                    <Text style={styles.tableCelltext}>{item.remark}</Text>
                  </View>
                </View>
              )
            })}
          </View>

          {/* 合计行：数量合计 / 本币销货金额 / 本币销货税额 / 本币金额合计 */}
          <View style={[styles.flexRow, styles.leftBoarder, styles.rightBoarder, styles.bottomBoarder, { paddingHorizontal: 12, paddingVertical: 6 }]} wrap={false}>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printTotalQuantity')}：${numFormmater(totalQuantity)}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printSaleAmount')}：${totalAmount}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printSaleTax')}：${taxAmount}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printTotalAmount')}：${totalRealAmount}`}</Text>
            </View>
          </View>

          {/* 签收提示 / 联次说明 */}
          <View style={[styles.flexRow, { marginTop: 6 }]}>
            <View style={styles.flexRowItem}>
              <Text style={styles.tableCelltext}>{t('sale.orderPrintLeft', { fax })}</Text>
            </View>
            <View style={styles.flexRowItem}>
              <Text style={[styles.tableCelltext, { textAlign: 'right' }]}>{t('sale.orderPrintRight')}</Text>
            </View>
          </View>

          {/* 签核行：核准 / 仓库 / 业务 / 制单 / 客户签收 */}
          <View style={[styles.flexRow, { marginTop: 6 }]}>
            <View style={styles.bottomFlexItem}>
              <Text style={styles.signText}>{t('sale.printApprove')}: </Text>
              <View style={styles.signLine} />
            </View>
            <View style={styles.bottomFlexItem}>
              <Text style={styles.signText}>{t('sale.printWarehouse')}: </Text>
              <View style={styles.signLine} />
            </View>
            <View style={styles.bottomFlexItem}>
              <Text style={styles.signText}>{t('sale.printSales')}: </Text>
              <View style={styles.signLine} />
            </View>
            <View style={styles.bottomFlexItem}>
              <Text style={styles.signText}>{t('sale.printMaker')}: </Text>
              <View style={styles.signLine} />
            </View>
            <View style={styles.bottomFlexItem}>
              <Text style={styles.signText}>{t('sale.printCustomerSign')}: </Text>
              <View style={styles.signLine} />
            </View>
          </View>

          {/* 左上角 Logo */}
          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,
            height: 30,
            objectFit: "scale-down"
          }} />
        </Page>
      </Document>
    }
    else return <Document />
  }

  // 竖向打印文档
  const MyVerticalDocument = (selRows: SaleItemState[]) => {

    return <Document>
      <Page size={{ width: printWitdh, height: printHeight }} orientation="portrait" style={[styles.page, styles.pageWraperPortrait]} wrap >
        {/* 此布局逻辑不需要补横线了 */}
        {/* <View fixed render={({ pageNumber }) => {
          return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000" }} />
        }}>
        </View> */}

        {/* 标题部分 */}
        <View style={styles.titleSection}>
          <Text style={styles.headerTitle}>{name ?? '吉昂科技（深圳）有限公司'}</Text>
          <Text style={styles.subTitle}>{t('sale.domesticSaleOrder')}</Text>
        </View>

        {/* 制表日期 / 页次 */}
        <View style={[styles.flexRow, { marginBottom: 4 }]}>
          <View style={styles.flexRowItem}>
            <Text style={styles.flexInfoText}>{`${t('sale.printDate')}: ${dayjs().format('YYYY/MM/DD')}`}</Text>
          </View>
          <View style={styles.flexRowItem}>
            <Text style={[styles.flexInfoText, { textAlign: 'right' }]} render={({ pageNumber, totalPages }) => `${t('sale.printPageNo')}: ${pageNumber}/${totalPages}`}>{''}</Text>
          </View>
        </View>

        {/* 表格 */}
        <View style={styles.table}>
          {/* 第一行：销货单号 / 客户全名 / 统一称号 */}
          <View style={[styles.tableRow, styles.topBoarder]} wrap={false}>
            <View style={[styles.tableCol, styles.noBottomBoarder, { width: '20%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printSaleNo')}：${orderNumber}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, { width: '50%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printCustomerName')}：${customerName}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, styles.rightBoarder, { width: '30%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printUnifiedTitle')}：${licenseId}`}</Text>
            </View>
          </View>
          {/* 第二行：单据日期 / 送货地址 / 联系电话 */}
          <View style={styles.tableRow} wrap={false}>
            <View style={[styles.tableCol, styles.noBottomBoarder, { width: '20%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printOrderDate')}：${orderDate}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, { width: '50%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printSendAddress')}：${sendAddress}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, styles.rightBoarder, { width: '30%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printPhone')}：${phone}`}</Text>
            </View>
          </View>
          {/* 第三行：发票号码 / 发票地址 / 联系人 */}
          <View style={styles.tableRow} wrap={false}>
            <View style={[styles.tableCol, styles.noBottomBoarder, { width: '20%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printInvoiceNumber')}：${invoiceNumber}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.noBottomBoarder, { width: '50%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printInvoiceAddress')}：${invoiceAddress}`}</Text>
            </View>
            <View style={[styles.tableCol, styles.noLeftBoarder, styles.rightBoarder, { width: '30%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printContact')}：${contactor}`}</Text>
            </View>
          </View>
          {/* 第四行：备注 */}
          <View style={[styles.tableRow, styles.bottomBoarder]} wrap={false}>
            <View style={[styles.tableCol, styles.rightBoarder, { width: '100%' }]}>
              <Text style={styles.tableCelltext}>{`${t('sale.printRemark')}：${remark}`}</Text>
            </View>
          </View>
          {/* 第五行：明细表头 */}
          <View style={[styles.tableRow, styles.bottomBoarder]} wrap={false}>
            <View style={[styles.tableCol, { width: '6%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printSeq')}</Text>
            </View>
            <View style={[styles.tableCol, { width: '30%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printProductName')}</Text>
            </View>
            <View style={[styles.tableCol, { width: '10%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printQuantity')}</Text>
            </View>
            <View style={[styles.tableCol, { width: '10%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printBQuantity')}</Text>
            </View>
            <View style={[styles.tableCol, { width: '8%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printUom')}</Text>
            </View>
            <View style={[styles.tableCol, { width: '10%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printPrice')}</Text>
            </View>
            <View style={[styles.tableCol, { width: '12%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printAmount')}</Text>
            </View>
            <View style={[styles.tableCol, styles.rightBoarder, { width: '14%' }]}>
              <Text style={styles.tableHeadertext}>{t('sale.printRemark')}</Text>
            </View>
          </View>
          {/* 明细数据行 */}
          {selRows.map((item, rowIndex) => {
            let taxRate = Number(item?.order?.taxRate) ?? 0;
            let noRateSInfo = '';
            if (taxRate !== 0) {
              noRateSInfo = priceFormat(noRateAmount(item?.price ?? "", divided(taxRate + '', "100")))
            } else noRateSInfo = item?.price ?? "";
            let noRateInfo = '';
            if (taxRate !== 0) {
              noRateInfo = priceFormat(noRateAmount(item?.totalRealAmount ?? "", divided(taxRate + '', "100")))
            } else noRateInfo = item?.totalRealAmount ?? "";

            return (
              <View style={styles.tableRow} key={item.id ?? rowIndex} wrap={false}>
                <View style={[styles.tableCol, { width: '6%' }]}>
                  <Text style={styles.tableCelltext}>{String(rowIndex + 1).padStart(4, '0')}</Text>
                </View>
                <View style={[styles.tableCol, { width: '30%' }]}>
                  <Text style={styles.tableCelltext}>{item?.product?.name ?? item.productName ?? ''}</Text>
                </View>
                <View style={[styles.tableCol, { width: '10%' }]}>
                  <Text style={styles.tableCelltext}>{numFormmater(Number(item.quantity) || 0)}</Text>
                </View>
                <View style={[styles.tableCol, { width: '10%' }]}>
                  <Text style={styles.tableCelltext}>{numFormmater(Number(item.bquantity) || 0)}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.tableCelltext}>{item.product?.uom ?? ''}</Text>
                </View>
                <View style={[styles.tableCol, { width: '10%' }]}>
                  <Text style={styles.tableCelltext}>{noRateSInfo}</Text>
                </View>
                <View style={[styles.tableCol, { width: '12%' }]}>
                  <Text style={styles.tableCelltext}>{noRateInfo}</Text>
                </View>
                <View style={[styles.tableCol, styles.rightBoarder, { width: '14%' }]}>
                  <Text style={styles.tableCelltext}>{item.remark}</Text>
                </View>
              </View>
            )
          })}
        </View>

        {/* 合计行：数量合计 / 本币销货金额 / 本币销货税额 / 本币金额合计 */}
        <View style={[styles.flexRow, styles.leftBoarder, styles.rightBoarder, styles.bottomBoarder, { paddingHorizontal: 12, paddingVertical: 6 }]} wrap={false}>
          <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
            <Text style={styles.tableCelltext}>{`${t('sale.printTotalQuantity')}：${numFormmater(totalQuantity)}`}</Text>
          </View>
          <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
            <Text style={styles.tableCelltext}>{`${t('sale.printSaleAmount')}：${totalAmount}`}</Text>
          </View>
          <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
            <Text style={styles.tableCelltext}>{`${t('sale.printSaleTax')}：${taxAmount}`}</Text>
          </View>
          <View style={[styles.tableCol, styles.noLeftBoarder, styles.flexRowItem]}>
            <Text style={styles.tableCelltext}>{`${t('sale.printTotalAmount')}：${totalRealAmount}`}</Text>
          </View>
        </View>

        {/* 签收提示 / 联次说明 */}
        <View style={[styles.flexRow, { marginTop: 6 }]}>
          <View style={styles.flexRowItem}>
            <Text style={styles.tableCelltext}>{t('sale.orderPrintLeft', { fax })}</Text>
          </View>
          <View style={styles.flexRowItem}>
            <Text style={[styles.tableCelltext, { textAlign: 'right' }]}>{t('sale.orderPrintRight')}</Text>
          </View>
        </View>

        {/* 签核行：核准 / 仓库 / 业务 / 制单 / 客户签收 */}
        <View style={[styles.flexRow, { marginTop: 6 }]}>
          <View style={styles.bottomFlexItem}>
            <Text style={styles.signText}>{t('sale.printApprove')}: </Text>
            <View style={styles.signLine} />
          </View>
          <View style={styles.bottomFlexItem}>
            <Text style={styles.signText}>{t('sale.printWarehouse')}: </Text>
            <View style={styles.signLine} />
          </View>
          <View style={styles.bottomFlexItem}>
            <Text style={styles.signText}>{t('sale.printSales')}: </Text>
            <View style={styles.signLine} />
          </View>
          <View style={styles.bottomFlexItem}>
            <Text style={styles.signText}>{t('sale.printMaker')}: </Text>
            <View style={styles.signLine} />
          </View>
          <View style={styles.bottomFlexItem}>
            <Text style={styles.signText}>{t('sale.printCustomerSign')}: </Text>
            <View style={styles.signLine} />
          </View>
        </View>

        {/* 左上角 Logo */}
        <Image src={picUrl || zioncom} style={{
          position: "absolute",
          top: 4,
          left: 8,
          flexGrow: 1,
          height: 30,
          objectFit: "scale-down"
        }} />
      </Page>

    </Document >
  }

  const handlePDF = async (flag: boolean) => {
    if (selectedRows.length !== 1) {
      return notification.error({ message: t('sale.onlyOneSelectip') });
    }
    setLoading(true)
    onStart()
    let dataRows: SaleItemState[] = [];
    dataRows = selectedRows[0].items.map(item => ({ ...item, order: { ...selectedRows[0], items: [] } }))
    try {
      let pageEle = flag ? MyVerticalDocument(dataRows) : MyDocument(dataRows)
      if (!pageEle) {
        notification.error({ placement: 'top', message: t('purchase.printErr') });
        setLoading(false);
        return
      }
      // Generate PDF as Blob in the browser
      const blob = await pdf(pageEle).toBlob();
      // let name = `${selectedRows[0].number}-${printerStr}(${flag ? t('warehouse.verticalPrint') : t('warehouse.normalPrint')}).pdf`;
      // let docId = selectedRows[0].docId;
      // if (docId) {
      //   let file = await upload({ name, size: blob.size, file: blob });
      //   if (file) {
      //     let doc = await docAdd({ pId: docId, name, suffix: file.suffix, nodeType: DocTypeEnum.LEAF, fileId: file.id });
      //     // TODO: 如需打印记录回写，在此补充对应 API 调用
      //   }
      // }
      const pdfUrl = URL.createObjectURL(blob);
      // 创建一个 iframe 并将 PDF URL 作为其源
      const iframe = document.createElement('iframe');

      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将 iframe 添加到 body 中
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
        } finally {
          setLoading(false)
          onEnd()
        }
      };

    } catch (error) {
      console.error('打印失败:', error);
      setLoading(false)
      onEnd()
    }
  };

  return (
    <Dropdown trigger={["click"]}
      menu={{
        items: [
          {
            key: 'NORM',
            label: t('warehouse.normalPrint'),
            onClick: () => handlePDF(false),
          },
          {
            key: 'WARE',
            label: <Tooltip title={t('layout.printTip', { width: settingWitdh, height: settingHeight })}>{t('warehouse.verticalPrint')}</Tooltip>,
            onClick: () => handlePDF(true),
          },
        ],
      }}
    >
      <Button type="primary" loading={loading} disabled={disabled}>{t('sale.printSale')}</Button>
    </Dropdown>);
};

export default PrintSaleOrder;
